// src/modules/app-views/account/pages/Subscription/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************

import { SimpleFormProvider } from 'components/SimpleComponents/SimpleFormProvider';
import useZustand from 'hooks/useZustand';
import { Tabs } from 'antd';
import translateX from 'utils/translateX';
import { useEffect, useMemo, useState } from 'react';
import { useDocumentContext } from 'components/core/DocumentProvider';
import useFormInstance from 'hooks/useFormInstance';
import appService from 'services/appService';
import SimpleFormProviderInitializer from 'components/SimpleComponents/SimpleFormProviderInitializer';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import { buildInitialValues } from 'utils/mock';
import OrganizationRecordTab from 'modules/app-views/account/pages/Subscription/tabs/RecordTab';
import VoipGotoTab from 'modules/app-views/account/pages/Subscription/tabs/VoipGotoTab';
import SubscriptionTeamTab from 'modules/app-views/account/pages/Subscription/tabs/TeamTab';
import { mergeUrlParamsQuery } from 'utils/urlUtils';
import { API } from 'constants/Api';
import accountService from 'modules/app-views/account/service';
import authService from 'services/authService';
import CardHeaderWithCountry from 'layouts/Pages/PageHeader/CardHeaderWithCountry';
import { returnOnlyNumbers } from 'utils/helpers';
import axiosService from 'services/axiosService';
import entitiesService from 'services/entitiesService';
import fiscalService from 'services/fiscalService';
import useFiscalDataTracking from 'hooks/useFiscalDataTracking';
import app from 'configs/appResolver';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const isMockStep = false;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SubscriptionsDocument = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const {
    tab,
    initTab,
    handleTabChange,
    availableTabs,
    buildTabs,
    data,
    viewMode,
    registerTabFieldPrefixes,
  } = useDocumentContext();
  const storedUser = useZustand('auth', 'user');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const { upsert } = useZustand('global', 'useApi');
  const { formRef, form, isInitializedRef, excludeFieldsFromReset } = useFormInstance({ data });

  // Centralized fiscal data tracking hook
  const { getSavedFiscalData, updateSavedFiscalData } = useFiscalDataTracking(data);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const locale = appService.getLocale();

  // PERFORMANCE FIX: Memoize requiredFields to prevent context re-renders on every keystroke
  // Without memoization, a new array reference is created on every render,
  // causing SimpleFormProvider's context value to change and all consumers to re-render
  const requiredFields = useMemo(() => {
    const fields = [
      'countryCode',

      'organization',
      'profile',

      'cnpj.value',
      'name',
      'fullName',
      'phone.value',
      'email.value',
      'foundAt',

      'address.zip',
      'address.number',
      'address.countryCode',
    ];

    if (locale !== 'BR') {
      fields.push('taxId', 'address.address1', 'address.region', 'address.city');
    }

    return fields;
  }, [locale]);

  const tabsItems = useMemo(() => {
    const src = Array.isArray(availableTabs)
      ? availableTabs
      : availableTabs
        ? Object.values(availableTabs)
        : [];
    return src;
  }, [availableTabs]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // NOTE: useMemo instead of useState to react when data becomes available
  const initialValues = useMemo(() => buildInitialValues(isMockStep, data), [data]);
  const [disabledFields, setDisabledFields] = useState([]);
  const [state, setState] = useState({
    dataOrigin: 'new_data',
    entityType: 'company',
    entityProfile: ['subscription', 'company'],
    resources: app.PRESET?.NATIVE_RESOURCES || [],
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const childProps = {
      ...props,
      initTab,
      state,
      setState,
      viewMode,
    };
    const itemsLocal = buildTabsArray(childProps);
    buildTabs(itemsLocal);
  }, [state, initTab, viewMode]); // keep behavior

  // Register tab field prefixes for error navigation
  // When a validation error occurs, this mapping tells DocumentProvider which tab to navigate to
  useEffect(() => {
    registerTabFieldPrefixes?.({
      // All fiscalData fields belong to the 'fiscal' tab
      fiscalData: 'fiscal',
      // All other fields (default) belong to the 'record' tab (initTab)
    });
  }, [registerTabFieldPrefixes]);

  // Load resources from data when editing an existing subscription
  useEffect(() => {
    if (data?.resources?.length > 0) {
      setState(prev => ({ ...prev, resources: data.resources }));
    }
  }, [data?.resources]);

  // NOTE: savedFiscalDataRef initialization is now handled by useFiscalDataTracking hook

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = async ({ values, allValues, uniqueEntity }) => {
    // --------------------------------------------------------------------------------- //
    // 0 - Validate required resources (at least 1 must be selected)
    // --------------------------------------------------------------------------------- //
    const resources = state?.resources || [];
    if (resources.length === 0) {
      appService.notification({
        flag: 'warning',
        title: 'attention',
        description: 'at_least_one_resource_required',
      });

      // Scroll to ResourcesCard for user visibility
      // setTimeout(() => {
      //   const resourcesCard = document.getElementById('resources-card');
      //   if (resourcesCard) {
      //     resourcesCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
      //   }
      // }, 100);

      // Throw error to prevent SimpleFormProvider from changing viewMode
      throw new Error('VALIDATION_FAILED');
    }

    // 0.5 - Deferred GoTo disconnect (resource removed + save = disconnect)
    const GOTO_RESOURCE_UID = 'moduleresources_voip_goto';
    const wasGotoEnabled = data?.resources?.includes(GOTO_RESOURCE_UID);
    const isGotoNowDisabled = !resources.includes(GOTO_RESOURCE_UID);

    if (wasGotoEnabled && isGotoNowDisabled) {
      try {
        await axiosService.delete({ url: API.GOTO.DISCONNECT });
      } catch {
        // Non-blocking: if disconnect fails, still save subscription
      }
    }

    // Add resources to values
    values.resources = resources;

    // Ensure profile is always included (may not be in values if user didn't change it)
    if (!values.profile && allValues.profile) {
      values.profile = allValues.profile;
    }

    // binding the entity _id to this subscription
    // IMPORTANT: Use nullish coalescing (??) to preserve explicit null values
    // null means "no entity linked" (new entity from external API)
    // undefined means "not set, use fallback"
    const allValuesEntityId = allValues?.entityId;
    const dataEntityId = data?.entityId;

    // If allValues.entityId is explicitly null, it means user searched via external API
    // and we should NOT use the old entityId from data
    if (allValuesEntityId === null) {
      values.entityId = null;
    } else {
      values.entityId = allValuesEntityId ?? dataEntityId;
    }

    let subscriptionId = values?._id || allValues?._id || data?._id;
    let entityId = values.entityId; // Already calculated above

    if (!values.organization || values.organization !== allValues.organization) {
      values.organization =
        allValues.organization ||
        values.name ||
        allValues.name ||
        values.firstName ||
        allValues.firstName;
    }

    if (!subscriptionId) {
      values.team = [
        {
          accountId: storedUser?._id,
          profile: 'owner',
          granted: {},
          invitedAt: storedUser?._metadata?.audit?.createdAt || new Date().toISOString(),
          invitedByAccountId: storedUser._id,
          joinedAt: storedUser?._metadata?.audit?.createdAt || new Date().toISOString(),
          status: 2,
        },
      ];
    }

    try {
      // --------------------------------------------------------------------------------- //
      // 0 - Handle unique entity restoration (if user loaded an existing entity)
      // --------------------------------------------------------------------------------- //
      if (uniqueEntity?._id && uniqueEntity?.documentValue) {
        // Verify that the current document value matches the one used to find the entity
        const currentCnpj = returnOnlyNumbers(allValues?.cnpj?.value || values?.cnpj?.value);
        const uniqueEntityCnpj = returnOnlyNumbers(uniqueEntity.documentValue);

        if (currentCnpj === uniqueEntityCnpj) {
          // Restore the entity by clearing removal/archive metadata and ensuring entityProfile
          const entityRestoreData = {
            // Clear removal and archive audit fields
            '_metadata.audit.removedAt': null,
            '_metadata.audit.removedByAccountId': null,
            '_metadata.audit.filedAt': null,
            '_metadata.audit.filedByAccountId': null,
          };

          // Ensure 'company' is in entityProfile without duplicates
          const existingProfile = uniqueEntity.data?.entityProfile || [];
          const profileArray = Array.isArray(existingProfile) ? existingProfile : [existingProfile];
          if (!profileArray.includes('company')) {
            entityRestoreData.entityProfile = [...profileArray, 'company'];
          }

          // Preserve avatar, backColor, foreColor from current subscription to the entity
          // These fields were kept in the form when user clicked YES to load entity data
          if (allValues?.avatar) entityRestoreData.avatar = allValues.avatar;
          if (allValues?.backColor) entityRestoreData.backColor = allValues.backColor;
          if (allValues?.foreColor) entityRestoreData.foreColor = allValues.foreColor;

          // PUT to restore the entity
          // NOTE: Use axiosService.put directly to bypass subscription page's routing config
          // The shared upsert hook is configured for the subscription page and would pollute the URL
          const entityUrl = mergeUrlParamsQuery({
            url: API.ENTITIES.URL,
            params: { _id: uniqueEntity._id },
          });
          await axiosService.put({ url: entityUrl, data: entityRestoreData });

          // Use the unique entity's ID as the entityId for the subscription
          entityId = uniqueEntity._id;
          values.entityId = entityId;
        }
      }

      // --------------------------------------------------------------------------------- //
      // 1 - Create new entity if no entityId but has CNPJ (data from external API)
      // --------------------------------------------------------------------------------- //
      const hasCnpj = returnOnlyNumbers(allValues?.cnpj?.value || values?.cnpj?.value);

      if (!entityId && hasCnpj) {
        // Build entity data from form values
        const entityData = {
          // Entity identification
          entityType: 'company',
          entityProfile: ['company'],
          dataOrigin: 'external_api',

          // Manual origin (user registered via form, even if data came from API lookup)
          _metadata: { origin: 0 },

          // Company data
          name: allValues?.name || values?.name,
          fullName: allValues?.fullName || values?.fullName,
          foundedAt: allValues?.foundedAt || values?.foundedAt,

          // Documents
          cnpj: allValues?.cnpj || values?.cnpj,

          // Contacts
          phone: allValues?.phone || values?.phone,
          email: allValues?.email || values?.email,

          // Address
          address: allValues?.address || values?.address,

          // Visual
          avatar: allValues?.avatar || values?.avatar,
          backColor: allValues?.backColor || values?.backColor,
          foreColor: allValues?.foreColor || values?.foreColor,

          // Country
          countryCode: allValues?.countryCode || values?.countryCode || 'BR',
        };

        // POST to create the entity
        // Use mergeUrlParamsQuery to replace :_id with empty string for POST
        const entityUrl = mergeUrlParamsQuery({
          url: API.ENTITIES.URL,
          params: { _id: '' }, // Empty _id for POST (create)
        });

        // axiosService.post with returnAsObject: true (default) returns [doc, result]
        // where doc is the created entity document
        const [entityDoc] = await axiosService.post({
          url: entityUrl,
          data: entityData,
          returnAsObject: true,
        });

        entityId = entityDoc?._id;
        values.entityId = entityId;
      }

      // --------------------------------------------------------------------------------- //
      // 2 - save the subscription
      // --------------------------------------------------------------------------------- //
      // Track if entity was created in Section 1 (new entity from external API)
      // If so, we should NOT send entity fields again to avoid duplicates
      // NOTE: Use allValues?.entityId instead of data?.entityId because the entityId
      // is stored in the form values, not necessarily in the data prop from DocumentContext
      const entityWasJustCreated = !allValues?.entityId && entityId;

      // Check if entity already existed (will be updated separately via direct PUT)
      const entityAlreadyExisted = !!allValues?.entityId;

      // Determine what to send to the backend:
      // - If entity was JUST CREATED in Section 1: remove entity fields (already saved, avoid duplicates)
      // - If entity ALREADY EXISTED: remove entity fields (will be saved via direct PUT to avoid duplicates)
      // NOTE: Entity fields are always handled separately to prevent the subscription backend
      // decorator from creating duplicate entries in arrays (addresses, contacts, documents)
      let dataSetToSave = values;

      if (entityWasJustCreated || entityAlreadyExisted) {
        // Remove entity-specific fields from values before saving subscription
        // These fields were already saved to the entity in Section 1
        // Sending them again would cause the backend decorator to add duplicates to arrays
        const {
          // Entity identification fields
          entityType,
          entityProfile,
          dataOrigin,
          // Documents
          cnpj: _cnpj,
          cpf: _cpf,
          rg: _rg,
          cnh: _cnh,
          passport: _passport,
          taxId: _taxId,
          // Contacts
          phone: _phone,
          email: _email,
          // Address
          address: _address,
          // Person/Company data
          name: _name,
          fullName: _fullName,
          firstName: _firstName,
          lastName: _lastName,
          middleName: _middleName,
          birthdate: _birthdate,
          foundedAt: _foundedAt,
          gender: _gender,
          // Arrays (already transformed by backend on previous save)
          documents: _documents,
          contacts: _contacts,
          addresses: _addresses,
          attachments: _attachments,
          // Other entity fields
          uniqueKey: _uniqueKey,
          indexFields: _indexFields,
          // Fiscal data - remove on first save (tab is disabled, values may have invalid enum from external API)
          fiscalData: _fiscalData,
          // Visual (keep these for subscription avatar)
          // avatar, backColor, foreColor - keep for subscription
          // Spread the rest as subscription-only fields
          ...subscriptionOnlyValues
        } = values;

        dataSetToSave = subscriptionOnlyValues;
      }

      // Apply decorator to transform phone, email, address, documents, etc.
      // This adds _id and countryCode from original data (allValues) to identify which items to update
      // Without this, the backend would create duplicates instead of updating existing items
      const decoratedDataSet = entitiesService.decorator(dataSetToSave, allValues);

      // NOTE: Fiscal service validation is now done in each tab's toggle handler (NfceTab, NfeTab, NfseTab)
      // in the fiscal-config module. The validateFiscalServicesBeforeSave call was removed.

      // Show "saving" message
      await appService.message({ flag: 'i', text: 'saving_data', key: 'saveContext' });

      // --------------------------------------------------------------------------------- //
      // 2.5 - Build entity update data (used in both flows below)
      // --------------------------------------------------------------------------------- //
      let entityUpdateData = null;

      if (entityId && entityAlreadyExisted && !entityWasJustCreated) {
        entityUpdateData = {};

        // Entity identification
        if (values.name !== undefined) entityUpdateData.name = values.name;
        if (values.fullName !== undefined) entityUpdateData.fullName = values.fullName;
        if (values.foundedAt !== undefined) entityUpdateData.foundedAt = values.foundedAt;

        // Documents - apply decorator to preserve _id for updates
        // NOTE: Use 'data' (from DocumentContext) as dataBefore because it has the entity's
        // original arrays (documents, contacts, addresses) needed to find the _id.
        // 'allValues' comes from form values which only has singular fields (cnpj, phone, address)
        // not the original arrays.
        // NOTE: Only process if has actual content (not empty object {})
        const cnpjHasContent = values.cnpj?.value;

        if (cnpjHasContent) {
          entityUpdateData.cnpj = entitiesService.decorator({ cnpj: values.cnpj }, data).cnpj;
        }

        // Contacts - apply decorator to preserve _id for updates
        // NOTE: Only process if has actual content (not empty object {})
        const phoneHasContent = values.phone?.value;
        const emailHasContent = values.email?.value;

        if (phoneHasContent) {
          entityUpdateData.phone = entitiesService.decorator({ phone: values.phone }, data).phone;
        }
        if (emailHasContent) {
          entityUpdateData.email = entitiesService.decorator({ email: values.email }, data).email;
        }

        // Address - apply decorator to preserve _id for updates
        // NOTE: Only process address if it has actual content (not empty object {})
        // An empty address object would cause the backend to create a new empty address entry
        const addressHasContent =
          values.address &&
          typeof values.address === 'object' &&
          Object.keys(values.address).some(
            key =>
              !['_id', 'countryCode', 'addressMetadata'].includes(key) &&
              values.address[key] !== undefined &&
              values.address[key] !== null &&
              values.address[key] !== ''
          );

        if (addressHasContent) {
          entityUpdateData.address = entitiesService.decorator(
            { address: values.address },
            data
          ).address;
        }

        // Visual
        if (values.avatar !== undefined) entityUpdateData.avatar = values.avatar;
        if (values.backColor !== undefined) entityUpdateData.backColor = values.backColor;
        if (values.foreColor !== undefined) entityUpdateData.foreColor = values.foreColor;

        // Country
        if (values.countryCode !== undefined) entityUpdateData.countryCode = values.countryCode;

        // Fiscal data - normalize to convert cleared fields to null
        if (allValues?.fiscalData !== undefined) {
          entityUpdateData.fiscalData = fiscalService.normalizeFiscalData(
            allValues.fiscalData,
            getSavedFiscalData()
          );
        }

        // Clear if nothing to update
        if (Object.keys(entityUpdateData).length === 0) {
          entityUpdateData = null;
        }
      }

      // --------------------------------------------------------------------------------- //
      // 2.6 - Activate/deactivate operators (appmulti only)
      // --------------------------------------------------------------------------------- //
      // For EDITING: runs BEFORE upsert because upsert reloads data into Zustand,
      // which re-mounts OperatorsCard and fetches the list again.
      // For CREATING: runs AFTER upsert so we can use the new subscriptionId.
      if (subscriptionId && app.MODULES?.includes('appmultiOperators')) {
        await replicateOperators(state);
      }

      // --------------------------------------------------------------------------------- //
      // 2.7 - Save subscription and entity (order depends on whether subscriptionId exists)
      // --------------------------------------------------------------------------------- //
      // IMPORTANT: When editing (subscriptionId exists), we update entity FIRST to ensure
      // fiscalData is saved before upsert reloads data into Zustand. This prevents the
      // "flash" of old values when clearing fiscal fields.
      // When creating (no subscriptionId), we need to create subscription first to get the ID.

      if (subscriptionId) {
        // EDITING: Update entity first, then subscription
        // This ensures fiscalData is saved before upsert reloads data

        // Update entity if there's data to update
        if (entityUpdateData) {
          const entityUrl = mergeUrlParamsQuery({
            url: API.ENTITIES.URL,
            params: { _id: entityId },
          });
          await axiosService.put({ url: entityUrl, data: entityUpdateData });
        }

        // Handle fiscal field clearing BEFORE upsert (which reloads data)
        if (allValues?.fiscalData) {
          const originalFiscalData = getSavedFiscalData();
          const clearedFields = fiscalService.getClearedFiscalFields(
            allValues.fiscalData,
            originalFiscalData
          );
          if (clearedFields.length > 0) {
            excludeFieldsFromReset(clearedFields);
          }
          updateSavedFiscalData(allValues.fiscalData);
        }

        // Now update subscription (this reloads data into Zustand)
        await upsert({ _id: subscriptionId, dataSet: decoratedDataSet });
      } else {
        // CREATING: Create subscription first (to get ID), then update entity
        const newDoc = await upsert({ _id: subscriptionId, dataSet: decoratedDataSet });

        // Replicate operators using the NEW subscriptionId (not the session header)
        const newSubId = newDoc?._id;
        if (newSubId && app.MODULES?.includes('appmultiOperators')) {
          await replicateOperators(state, {
            'x-subscriptionid': newSubId,
          });
        }

        // Update entity after subscription is created
        if (entityUpdateData) {
          const entityUrl = mergeUrlParamsQuery({
            url: API.ENTITIES.URL,
            params: { _id: entityId },
          });
          await axiosService.put({ url: entityUrl, data: entityUpdateData });
        } else if (entityId && allValues?.fiscalData) {
          // Fallback: update just fiscalData if entity was just created
          const entityFiscalUrl = mergeUrlParamsQuery({
            url: API.ENTITIES.URL,
            params: { _id: entityId },
          });
          await axiosService.put({
            url: entityFiscalUrl,
            data: {
              fiscalData: fiscalService.normalizeFiscalData(
                allValues.fiscalData,
                getSavedFiscalData()
              ),
            },
          });
        }
      }

      // --------------------------------------------------------------------------------- //
      // 3 - Complete welcome next steps and refresh auth
      // --------------------------------------------------------------------------------- //
      const nextStep = 'create_your_first_subscription';
      if (!subscriptionId && !accountService.hasNextStep(nextStep)) {
        await accountService.putNextStep(nextStep);
      } else {
        await authService.signInRefresh();
      }

      // Clear form fields after signInRefresh (excluded fields won't flash back)
      if (allValues?.fiscalData) {
        fiscalService.clearFiscalFieldsInForm(form, allValues.fiscalData, getSavedFiscalData());
      }
    } catch (err) {
      console.error('[Subscription.handleFinish] error =>', err);
      throw err;
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="container mt-3">
        <SimpleFormProvider
          formRef={formRef}
          onFinish={handleFinish}
          showSubmitButton={false}
          initFocus={!data?._id && 'organization'}
          // initFocus={locale === 'BR' ? 'cnpj.value' : 'name'}
          additionalValues={{
            entityProfile: 'company',
          }}
          // reloadEntirePage={false}
          ignoreResetFormOnSubmit={true}
          requiredFields={requiredFields}
          disabledFields={disabledFields}
          locked={viewMode === 'view'}
          showInfoMessage={false}
          // forceViewAfterSave={false}
          initialValues={initialValues}
          data={data}
        >
          <SimpleFormProviderInitializer data={data} isInitializedRef={isInitializedRef} />

          <div style={{ position: 'relative' }}>
            <CardHeaderWithCountry
              showAvatar
              data={data}
              locate="pageSettings"
              avatarName={data?.organization}
              watchField="countryCode"
            />

            <SimpleLoadingArea
              name="cardHeader"
              offsetTop={0}
              showProgress={false}
              zIndex={100}
              borderRadius={8}
            />
          </div>

          <Tabs
            type="line"
            tabPlacement="top"
            activeKey={tab}
            rootClassName="tab-in-form no-content-padding"
            items={tabsItems}
            onChange={handleTabChange}
            animated={{ inkBar: true, tabPane: false }}
            destroyOnHidden={false}
          />
        </SimpleFormProvider>
      </div>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SubscriptionsDocument;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildTabsArray(props) {
  const { initTab, viewMode, state } = props;
  const hasVoipGoto = state?.resources?.includes('moduleresources_voip_goto');

  return [
    {
      key: initTab,
      label: <span>{translateX('record')}</span>,
      hide: false,
      disabled: false,
      children: <OrganizationRecordTab {...props} tabName={initTab} />,
    },
    {
      key: 'voipGoto',
      label: <span>{translateX('resource_voip_goto')}</span>,
      hide: !hasVoipGoto,
      disabled: viewMode === 'add',
      children: <VoipGotoTab {...props} tabName="voipGoto" />,
    },
    // {
    //   key: 'fiscal',
    //   label: <span>{translateX('fiscal')}</span>,
    //   hide: false,
    //   disabled: viewMode === 'add',
    //   children: <FiscalTab {...props} tabName="fiscal" />,
    // },
    // {
    //   key: 'integrations',
    //   label: <span>{translateX('integrations')}</span>,
    //   hide: false,
    //   disabled: viewMode === 'add',
    //   children: <ParametersTab {...props} tabName="integrations" />,
    // },
    {
      key: 'users',
      label: <span>{translateX('users')}</span>,
      hide: false,
      disabled: viewMode === 'add',
      children: <SubscriptionTeamTab {...props} tabName="users" />,
    },
    // {
    //   key: 'resources',
    //   label: <span>{translateX('resource_configuration')}</span>,
    //   hide: false,
    //   disabled: viewMode === 'add',
    //   children: <ResourcesTab {...props} tabName="resources" />,
    // },
  ];
}

function buildEntityPayload({ data = {}, state = {}, ...rest }) {
  return {
    ...rest,
    ...data,
    ...state,
    name: data?.name,
    fullName: data?.fullName || data?.name,
    country: data?.country || 'BR',
  };
}

function buildSubscriptionPayload(data = {}) {
  const storedUser = useZustand('auth', 'user', false);

  if (!data?._id && storedUser?._id) {
    data.team = [
      {
        accountId: storedUser._id,
        profile: 'owner',
        granted: {},
        invitedAt: storedUser?._metadata?.audit?.createdAt || new Date().toISOString(),
        invitedByAccountId: storedUser._id,
        joinedAt: storedUser?._metadata?.audit?.createdAt || new Date().toISOString(),
        status: 2,
      },
    ];
  }

  return data;
}

// Replicate/deactivate operators based on selection changes
// @param {Object} state - Component state with operatorActiveIds and operatorReplicatedIds
// @param {Object} [headers] - Optional custom headers (e.g. x-subscriptionid for new subscriptions)
async function replicateOperators(state, headers) {
  const activeIds = state?.operatorActiveIds || [];
  const replicatedIds = state?.operatorReplicatedIds || [];

  const toActivate = activeIds.filter(id => !replicatedIds.includes(id));
  const toDeactivate = replicatedIds.filter(id => !activeIds.includes(id));

  const promises = [];

  if (toActivate.length > 0) {
    promises.push(
      axiosService.post({
        url: API.SYS_OPERATORS_API.REPLICATE,
        data: { seedEntityIds: toActivate },
        ...(headers && { headers }),
      }),
    );
  }

  if (toDeactivate.length > 0) {
    promises.push(
      axiosService.post({
        url: API.SYS_OPERATORS_API.DEACTIVATE,
        data: { seedEntityIds: toDeactivate },
        ...(headers && { headers }),
      }),
    );
  }

  if (promises.length > 0) {
    await Promise.all(promises);
  }
}

// Fiscal validation and normalization helpers moved to fiscalService.js
// Use: fiscalService.normalizeFiscalData, fiscalService.clearFiscalFieldsInForm
